(function () {
  'use strict';

  var SUGGEST_CACHE_TTL_MS = 60 * 1000;
  var DEBOUNCE_MS = 250;
  var PLACEHOLDER_INTERVAL_MS = 2200;
  var RECENT_KEY = 'idam-search:recent';
  var RECENT_MAX = 6;
  // The theme already records viewed product handles here (assets/theme.js,
  // "RECENTLY VIEWED PRODUCTS"), newest first, capped at 20 — so we read that
  // list instead of tracking our own. Product JSON is memoised per tab.
  var VIEWED_KEY = 'recently_viewed_products';
  var VIEWED_CACHE_KEY = 'idam-search:viewed-cache';

  /* ----------------------------------------------------------------- utils */
  function moneyFromFormat(amount, format) {
    // amount is a Number (major units, e.g. 599). Shopify money_format uses
    // {{amount}} placeholders expecting cents; we only need a readable price.
    if (amount == null || amount === '' || isNaN(amount)) return '';
    var n = Math.round(Number(amount));
    if (format && format.indexOf('{{') !== -1) {
      // The leading part of a money_format can include HTML (e.g.
      // `<span class="money">₹{{amount}}</span>`); strip tags so only the
      // currency symbol remains — otherwise an unclosed <span> leaks through.
      var symbol = (format.match(/^[^{]*/) || [''])[0].replace(/<[^>]*>/g, '').trim();
      return (symbol || '₹') + n.toLocaleString('en-IN');
    }
    return '₹' + n.toLocaleString('en-IN');
  }

  function suggestImageUrl(image) {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.url || image.src || '';
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function parseLines(value) {
    if (!value) return [];
    return value.split('\n').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  function getRecent() {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); }
    catch (e) { return []; }
  }

  function pushRecent(term) {
    if (!term) return;
    try {
      var list = getRecent().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
      list.unshift(term);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) { /* ignore */ }
  }

  function currentProductHandle() {
    var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  function getViewedHandles() {
    var list;
    try { list = JSON.parse(localStorage.getItem(VIEWED_KEY) || '[]'); }
    catch (e) { return []; }
    if (!Array.isArray(list)) return [];
    var current = currentProductHandle();
    return list.filter(function (h) {
      return h && typeof h === 'string' && h !== current;
    });
  }

  function readViewedCache() {
    try { return JSON.parse(sessionStorage.getItem(VIEWED_CACHE_KEY) || '{}') || {}; }
    catch (e) { return {}; }
  }

  function writeViewedCache(cache) {
    try { sessionStorage.setItem(VIEWED_CACHE_KEY, JSON.stringify(cache)); }
    catch (e) { /* ignore */ }
  }

  function sizedImage(url, size) {
    if (!url) return '';
    return url.replace(/(\.(?:jpg|jpeg|png|webp))(\?|$)/i, '_' + size + 'x$1$2');
  }

  /* ------------------------------------------------------------- component */
  function SearchWidget(root) {
    this.root = root;
    this.baseUrl = (root.getAttribute('data-backend-base-url') || '').replace(/\/$/, '');
    this.shopDomain = root.getAttribute('data-shop-domain') || '';
    this.resultsPath = root.getAttribute('data-results-path') || '/search';
    this.suggestLimit = parseInt(root.getAttribute('data-suggest-limit'), 10) || 5;
    this.moneyFormat = root.getAttribute('data-money-format') || '';
    this.showTrending = root.getAttribute('data-show-trending') === 'true';
    this.trendingTerms = parseLines(root.getAttribute('data-trending-terms'));
    this.trendingIcon = root.getAttribute('data-trending-icon') || '';
    this.placeholders = parseLines(root.getAttribute('data-placeholders'));
    this.sendEvents = root.getAttribute('data-send-events') === 'true';
    this.showViewed = root.getAttribute('data-show-recently-viewed') === 'true';
    this.viewedLimit = parseInt(root.getAttribute('data-recently-viewed-limit'), 10) || 8;
    this.showCollections = root.getAttribute('data-show-collection-results') === 'true';
    this.collectionLimit = parseInt(root.getAttribute('data-collection-limit'), 10) || 4;
    this.predictiveUrl = root.getAttribute('data-predictive-url') || '/search/suggest';

    this.inputs = Array.prototype.slice.call(root.querySelectorAll('[data-idam-search-input]'));
    this.clearBtns = Array.prototype.slice.call(root.querySelectorAll('[data-idam-search-clear]'));
    this.forms = Array.prototype.slice.call(root.querySelectorAll('[data-idam-search-form]'));
    this.overlay = root.querySelector('[data-idam-search-overlay]');
    this.popup = root.querySelector('[data-idam-search-popup]');
    this.closeBtn = root.querySelector('[data-idam-search-close]');
    this.bodyEmpty = root.querySelector('[data-idam-search-empty]');
    this.suggestWrap = root.querySelector('[data-idam-search-suggest]');
    this.suggestList = root.querySelector('[data-idam-search-suggest-list]');
    this.productsWrap = root.querySelector('[data-idam-search-products]');
    this.collectionsWrap = root.querySelector('[data-idam-search-collections]');
    this.collectionsList = root.querySelector('[data-idam-search-collections-list]');
    this.searchForBtn = root.querySelector('[data-idam-search-submit]');
    this.statusEl = root.querySelector('[data-idam-search-status]');
    this.trendingWrap = root.querySelector('[data-idam-search-trending]');
    this.trendingList = root.querySelector('[data-idam-search-trending-list]');
    this.recentWrap = root.querySelector('[data-idam-search-recent]');
    this.recentList = root.querySelector('[data-idam-search-recent-list]');
    this.viewedWrap = root.querySelector('[data-idam-search-viewed]');
    this.viewedList = root.querySelector('[data-idam-search-viewed-list]');

    this.cache = {};
    this.activeRequestId = 0;
    this.abortCtrl = null;
    this.isOpen = false;
    this.scrollY = 0;
    this.term = '';
    this.placeholderIdx = 0;
    this.placeholderTimer = null;
    this.viewedProducts = null;
    this.viewedLoading = false;
    this.viewedPainted = false;
  }

  SearchWidget.prototype.init = function () {
    if (!this.baseUrl) {
      // Backend not configured — leave hidden (graceful failure).
      console.warn('[idam-search] data-backend-base-url is empty; search hidden.');
      return;
    }
    this.root.removeAttribute('hidden');

    var self = this;
    var debounced = debounce(function () { self.onQuery(); }, DEBOUNCE_MS);

    this.inputs.forEach(function (input) {
      input.addEventListener('focus', function () { self.open(); });
      input.addEventListener('input', function () {
        self.syncInputs(input.value);
        self.toggleClear();
        if (!self.isOpen) self.open();
        debounced();
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); self.submit(input.value); }
        else if (e.key === 'Escape') { self.close(); }
      });
    });

    this.clearBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.syncInputs('');
        self.toggleClear();
        self.onQuery();
        var first = self.inputs[0];
        if (first) first.focus();
      });
    });

    this.forms.forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var term = self.currentValue();
        // The magnifier is the submit button, so a click with an empty field
        // lands here. Open the panel rather than silently doing nothing.
        if (!term) { self.open(); return; }
        self.submit(term);
      });
    });

    if (this.overlay) this.overlay.addEventListener('click', function () { self.close(); });
    if (this.closeBtn) this.closeBtn.addEventListener('click', function () { self.close(); });
    if (this.searchForBtn) {
      this.searchForBtn.addEventListener('click', function () { self.submit(self.currentValue()); });
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.isOpen) self.close();
    });

    this.renderEmptyState();
    this.startPlaceholderCycle();
  };

  SearchWidget.prototype.currentValue = function () {
    return this.inputs.length ? this.inputs[0].value.trim() : '';
  };

  SearchWidget.prototype.syncInputs = function (value) {
    this.inputs.forEach(function (input) { if (input.value !== value) input.value = value; });
  };

  SearchWidget.prototype.toggleClear = function () {
    var has = this.currentValue().length > 0;
    this.clearBtns.forEach(function (b) { b.hidden = !has; });
  };

  /* ---- placeholder animation ------------------------------------------- */
  SearchWidget.prototype.startPlaceholderCycle = function () {
    if (!this.placeholders.length) return;
    var self = this;
    var base = 'Search for ';
    function tick() {
      var phrase = self.placeholders[self.placeholderIdx % self.placeholders.length];
      self.inputs.forEach(function (input) {
        if (!input.value) input.setAttribute('placeholder', base + phrase);
      });
      self.placeholderIdx++;
    }
    tick();
    this.placeholderTimer = setInterval(tick, PLACEHOLDER_INTERVAL_MS);
  };

  /* ---- open / close ----------------------------------------------------- */
  SearchWidget.prototype.open = function () {
    if (this.isOpen) return;
    this.isOpen = true;
    this.scrollY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.classList.add('idam-search-locked');
    if (this.overlay) this.overlay.hidden = false;
    if (this.popup) this.popup.hidden = false;
    // Move focus to the popup input.
    var popupInput = this.popup ? this.popup.querySelector('[data-idam-search-input]') : null;
    if (popupInput) { popupInput.value = this.currentValue(); setTimeout(function () { popupInput.focus(); }, 0); }
    this.onQuery();
  };

  SearchWidget.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;
    document.documentElement.classList.remove('idam-search-locked');
    if (this.overlay) this.overlay.hidden = true;
    if (this.popup) this.popup.hidden = true;
  };

  /* ---- query handling --------------------------------------------------- */
  SearchWidget.prototype.onQuery = function () {
    var term = this.currentValue();
    this.term = term;
    this.toggleClear();

    if (!term) {
      this.showEmpty();
      return;
    }
    this.fetchSuggest(term);
  };

  SearchWidget.prototype.showEmpty = function () {
    if (this.suggestWrap) this.suggestWrap.hidden = true;
    if (this.statusEl) this.statusEl.hidden = true;
    if (this.bodyEmpty) this.bodyEmpty.hidden = false;
    this.renderEmptyState();
  };

  SearchWidget.prototype.renderEmptyState = function () {
    var self = this;
    if (this.showTrending && this.trendingWrap && this.trendingList) {
      if (this.trendingTerms.length) {
        this.trendingList.innerHTML = '';
        this.trendingTerms.forEach(function (t) { self.trendingList.appendChild(self.chip(t, self.trendingIcon, 'trending')); });
        this.trendingWrap.hidden = false;
      } else {
        this.trendingWrap.hidden = true;
      }
    }
    if (this.recentWrap && this.recentList) {
      var recent = getRecent();
      if (recent.length) {
        this.recentList.innerHTML = '';
        recent.forEach(function (t) { self.recentList.appendChild(self.chip(t)); });
        this.recentWrap.hidden = false;
      } else {
        this.recentWrap.hidden = true;
      }
    }
    this.renderViewed();
  };

  /* ---- recently viewed products ----------------------------------------- */
  SearchWidget.prototype.renderViewed = function () {
    if (!this.showViewed || !this.viewedWrap || !this.viewedList) return;
    // Painted once per page load; showEmpty() runs on every keystroke that
    // empties the input, and rebuilding the cards there would flicker.
    if (this.viewedPainted || this.viewedLoading) return;
    if (this.viewedProducts) { this.paintViewed(this.viewedProducts); return; }

    var handles = getViewedHandles().slice(0, this.viewedLimit);
    if (!handles.length) { this.viewedWrap.hidden = true; return; }

    var self = this;
    var cache = readViewedCache();
    this.viewedLoading = true;

    Promise.all(handles.map(function (handle) {
      if (cache[handle]) return cache[handle];
      return fetch('/products/' + encodeURIComponent(handle) + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p) return null;
          // Store a lean record — the full product JSON is far too big for
          // sessionStorage once a shopper has browsed a few items.
          cache[handle] = {
            handle: p.handle,
            title: p.title,
            url: p.url || '/products/' + p.handle,
            image: p.featured_image || '',
            price: p.price,
            compareAtPrice: p.compare_at_price
          };
          return cache[handle];
        })
        .catch(function () { return null; });
    })).then(function (products) {
      self.viewedLoading = false;
      // Drop anything that 404'd (unpublished or deleted) but keep the
      // newest-first order the theme stored them in.
      self.viewedProducts = products.filter(Boolean);
      writeViewedCache(cache);
      self.paintViewed(self.viewedProducts);
    });
  };

  SearchWidget.prototype.paintViewed = function (products) {
    if (!this.viewedWrap || !this.viewedList) return;
    if (!products.length) { this.viewedWrap.hidden = true; return; }
    var self = this;
    this.viewedList.innerHTML = '';
    products.forEach(function (p) { self.viewedList.appendChild(self.viewedCard(p)); });
    this.viewedWrap.hidden = false;
    this.viewedPainted = true;
  };

  SearchWidget.prototype.viewedCard = function (product) {
    var li = document.createElement('li');
    li.className = 'idam-search__viewed-item';

    var link = document.createElement('a');
    link.className = 'idam-search__viewed-link';
    link.href = product.url || '/products/' + product.handle;

    var media = document.createElement('span');
    media.className = 'idam-search__viewed-media';
    var src = sizedImage(product.image, 360);
    if (src) {
      var img = document.createElement('img');
      img.src = src;
      img.alt = product.title || '';
      img.loading = 'lazy';
      media.appendChild(img);
    }
    link.appendChild(media);

    var title = document.createElement('span');
    title.className = 'idam-search__viewed-title';
    title.textContent = product.title || '';
    link.appendChild(title);

    var prices = document.createElement('span');
    prices.className = 'idam-search__viewed-prices';

    // /products/{handle}.js reports money in cents.
    var price = (product.price == null) ? null : Number(product.price) / 100;
    var compare = (product.compareAtPrice == null) ? null : Number(product.compareAtPrice) / 100;

    if (price != null) {
      var priceEl = document.createElement('span');
      priceEl.className = 'idam-search__viewed-price';
      priceEl.textContent = moneyFromFormat(price, this.moneyFormat);
      prices.appendChild(priceEl);
    }

    if (price != null && compare != null && compare > price) {
      var compareEl = document.createElement('span');
      compareEl.className = 'idam-search__viewed-compare';
      compareEl.textContent = moneyFromFormat(compare, this.moneyFormat);
      prices.appendChild(compareEl);

      var offEl = document.createElement('span');
      offEl.className = 'idam-search__viewed-off';
      offEl.textContent = Math.round((compare - price) / compare * 100) + '% off';
      prices.appendChild(offEl);
    }

    link.appendChild(prices);
    li.appendChild(link);
    return li;
  };

  // `selectionType` is the analytics bucket for the chip — only "trending" is
  // a value the events API accepts, so recent-search chips pass nothing.
  SearchWidget.prototype.chip = function (term, iconUrl, selectionType) {
    var self = this;
    var li = document.createElement('li');
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'idam-search__chip';
    if (iconUrl) {
      btn.className += ' idam-search__chip--trending';
      var icon = document.createElement('img');
      icon.className = 'idam-search__chip-icon';
      icon.src = iconUrl;
      icon.alt = '';
      icon.setAttribute('aria-hidden', 'true');
      icon.width = 14;
      icon.height = 14;
      btn.appendChild(icon);
      // Assigning textContent here would wipe the icon, so the term gets its
      // own node.
      var label = document.createElement('span');
      label.textContent = term;
      btn.appendChild(label);
    } else {
      btn.textContent = term;
    }
    btn.addEventListener('click', function () {
      if (selectionType) {
        self.track({
          eventType: 'autocomplete_click',
          // No typed query behind a chip on the empty state — the term the
          // shopper picked doubles as the query.
          query: self.term || term,
          selectedValue: term,
          selectionType: selectionType
        }, true);
      }
      self.syncInputs(term);
      self.submit(term);
    });
    li.appendChild(btn);
    return li;
  };

  SearchWidget.prototype.fetchSuggest = function (term) {
    var self = this;
    var key = term.toLowerCase();
    var cached = this.cache[key];
    if (cached && (Date.now() - cached.t) < SUGGEST_CACHE_TTL_MS) {
      this.renderSuggest(cached.products, cached.collections, term);
      return;
    }

    if (this.bodyEmpty) this.bodyEmpty.hidden = true;
    if (this.suggestWrap) this.suggestWrap.hidden = true;
    if (this.statusEl) { this.statusEl.hidden = false; this.statusEl.textContent = 'Searching…'; }

    var reqId = ++this.activeRequestId;
    if (this.abortCtrl) this.abortCtrl.abort();
    var ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    this.abortCtrl = ctrl;

    // Products and collections come from two different services, so they are
    // fetched together and rendered in one pass — painting collections as soon
    // as they land would shove the product list down mid-read.
    Promise.all([
      this.fetchProducts(term, ctrl),
      this.fetchCollections(term, ctrl)
    ]).then(function (res) {
      if (reqId !== self.activeRequestId) return; // stale (superseded or aborted)
      var products = res[0];
      var collections = res[1];
      self.cache[key] = { t: Date.now(), products: products, collections: collections };
      self.renderSuggest(products, collections, term);
      // No event here on purpose: the backend records a `suggest` event with
      // the result count and response time on every /search/suggest call.
    });
  };

  // Resolves to an array, never rejects — a failed leg just renders as empty
  // so one dead service can't blank out the other's results.
  SearchWidget.prototype.fetchProducts = function (term, ctrl) {
    var url = this.baseUrl + '/api/v1/search/suggest?q=' + encodeURIComponent(term) + '&limit=' + this.suggestLimit;
    var opts = {
      method: 'GET',
      headers: { 'X-Shop-Domain': this.shopDomain, 'Accept': 'application/json' }
    };
    if (ctrl) opts.signal = ctrl.signal;

    return fetch(url, opts)
      .then(function (r) { return r.json(); })
      .then(function (body) {
        var data = (body && body.success && body.data) ? body.data : (body && body.data) || [];
        return Array.isArray(data) ? data : [];
      })
      .catch(function () { return []; });
  };

  // Shopify's own predictive search — same origin, locale-aware, and it already
  // knows the store's collections, so the idam backend needs no change.
  SearchWidget.prototype.fetchCollections = function (term, ctrl) {
    if (!this.showCollections || !this.collectionsList) return Promise.resolve([]);

    var url = this.predictiveUrl + '.json?q=' + encodeURIComponent(term)
      + '&resources[type]=collection&resources[limit]=' + this.collectionLimit;
    var opts = { method: 'GET', headers: { 'Accept': 'application/json' } };
    if (ctrl) opts.signal = ctrl.signal;

    return fetch(url, opts)
      .then(function (r) {
        if (!r.ok) {
          console.warn('[idam-search] collections lookup failed: HTTP ' + r.status + ' for ' + url);
          return null;
        }
        return r.json();
      })
      .then(function (body) {
        var results = body && body.resources && body.resources.results;
        var list = (results && results.collections) || [];
        return list.filter(function (c) { return c && c.title && c.url; });
      })
      .catch(function (err) {
        // AbortError fires on every superseded keystroke — that one is normal.
        if (!err || err.name !== 'AbortError') {
          console.warn('[idam-search] collections lookup errored:', err);
        }
        return [];
      });
  };

  SearchWidget.prototype.renderSuggest = function (items, collections, term) {
    if (this.bodyEmpty) this.bodyEmpty.hidden = true;
    if (this.statusEl) this.statusEl.hidden = true;
    if (!this.suggestWrap || !this.suggestList) return;

    var self = this;
    items = items || [];
    collections = collections || [];

    // Collections first — they are the broader, more useful jump for a partial
    // term ("earring" -> the Earrings collection beats five random earrings).
    if (this.collectionsWrap && this.collectionsList) {
      this.collectionsList.innerHTML = '';
      collections.forEach(function (c, i) { self.collectionsList.appendChild(self.collectionChip(c, i)); });
      this.collectionsWrap.hidden = !collections.length;
    }

    this.suggestList.innerHTML = '';
    items.forEach(function (item, i) { self.suggestList.appendChild(self.suggestItem(item, i)); });
    if (this.productsWrap) this.productsWrap.hidden = !items.length;

    if (!items.length && !collections.length) {
      this.suggestWrap.hidden = true;
      if (this.statusEl) { this.statusEl.hidden = false; this.statusEl.textContent = ""; }
      this.showSearchFor(term);
      return;
    }

    this.suggestWrap.hidden = false;
    this.showSearchFor(term);
  };

  SearchWidget.prototype.collectionChip = function (collection, index) {
    var self = this;
    var li = document.createElement('li');
    // An anchor rather than a button so middle-click / open-in-new-tab work.
    var link = document.createElement('a');
    link.className = 'idam-search__chip idam-search__chip--collection';
    link.href = collection.url;
    link.textContent = collection.title;
    link.addEventListener('click', function () {
      self.track({
        eventType: 'autocomplete_click',
        query: self.term,
        // The API guide sends the label a shopper actually saw for a category
        // ("Lipstick"), and the handle as supporting data. Products are the
        // other way round — there the guide sends the handle.
        selectedValue: collection.title || collection.handle,
        // The events API only accepts category/product/trending; a Shopify
        // collection is this store's notion of a category.
        selectionType: 'category',
        metadata: { handle: collection.handle, position: (index || 0) + 1 }
      }, true);
    });
    li.appendChild(link);
    return li;
  };

  SearchWidget.prototype.showSearchFor = function (term) {
    if (!this.searchForBtn) return;
    this.searchForBtn.hidden = false;
    this.searchForBtn.textContent = 'Search for “' + term + '”';
    // ensure the button is inside the visible results group when empty too
    if (this.suggestWrap && this.suggestWrap.hidden && this.statusEl && !this.statusEl.hidden) {
      this.statusEl.appendChild(this.searchForBtn);
    } else if (this.suggestWrap) {
      this.suggestWrap.appendChild(this.searchForBtn);
    }
  };

  SearchWidget.prototype.suggestItem = function (item, index) {
    var self = this;
    var li = document.createElement('li');
    li.className = 'idam-search__suggest-item';
    li.setAttribute('role', 'option');

    var btn = document.createElement('button');
    btn.type = 'button';

    var imgUrl = suggestImageUrl(item.image);
    if (imgUrl) {
      var img = document.createElement('img');
      img.className = 'idam-search__suggest-thumb';
      img.src = imgUrl;
      img.alt = item.title || '';
      img.loading = 'lazy';
      btn.appendChild(img);
    } else {
      var ph = document.createElement('span');
      ph.className = 'idam-search__suggest-thumb';
      btn.appendChild(ph);
    }

    var meta = document.createElement('div');
    meta.className = 'idam-search__suggest-meta';

    var title = document.createElement('div');
    title.className = 'idam-search__suggest-title';
    title.textContent = item.title || '';
    meta.appendChild(title);

    var priceWrap = document.createElement('div');
    var price = (item.minPrice != null) ? item.minPrice : item.price;
    var priceSpan = document.createElement('span');
    priceSpan.className = 'idam-search__suggest-price';
    priceSpan.textContent = moneyFromFormat(price, this.moneyFormat);
    priceWrap.appendChild(priceSpan);
    if (item.compareAtPrice != null && Number(item.compareAtPrice) > Number(price)) {
      var cmp = document.createElement('span');
      cmp.className = 'idam-search__suggest-compare';
      cmp.textContent = moneyFromFormat(item.compareAtPrice, this.moneyFormat);
      priceWrap.appendChild(cmp);
    }
    meta.appendChild(priceWrap);

    btn.appendChild(meta);

    btn.addEventListener('click', function () {
      var position = (index || 0) + 1;
      self.track({
        eventType: 'autocomplete_click',
        query: self.term,
        selectedValue: item.handle || item.title,
        selectionType: 'product',
        metadata: { title: item.title, position: position }
      });
      if (item.handle) {
        // Also a product click off a search — `autocomplete_click` feeds the
        // autocomplete_usage metric and `click` feeds top_clicked_products, so
        // the two land in different buckets rather than double-counting one.
        self.track({
          eventType: 'click',
          query: self.term,
          productHandle: item.handle,
          position: position,
          metadata: { source: 'autocomplete' }
        }, true);
        self.attribute(self.term, item.handle);
        window.location.href = '/products/' + item.handle;
      } else {
        self.submit(item.title || self.term);
      }
    });

    li.appendChild(btn);
    return li;
  };

  SearchWidget.prototype.submit = function (term) {
    term = (term || '').trim();
    if (!term) return;
    pushRecent(term);
    // No `search` event here — the events API rejects it; searches are logged
    // server-side by whichever search endpoint serves the results.
    var sep = this.resultsPath.indexOf('?') === -1 ? '?' : '&';
    window.location.href = this.resultsPath + sep + 'q=' + encodeURIComponent(term);
  };

  // Event delivery (batching, session id, keepalive) lives in
  // assets/idam-search-analytics.js so the results page and PDP share it.
  // `immediate` skips the batch window for clicks that navigate away.
  SearchWidget.prototype.track = function (event, immediate) {
    if (!this.sendEvents) return;
    var analytics = window.IdamSearchAnalytics;
    if (!analytics || !analytics.track) return;
    analytics.track(event, immediate);
  };

  // Credits a later add-to-cart on the product page back to this search.
  SearchWidget.prototype.attribute = function (query, handle) {
    var analytics = window.IdamSearchAnalytics;
    if (analytics && analytics.rememberSearchClick) {
      analytics.rememberSearchClick(query, handle, 'autocomplete');
    }
  };

  /* ------------------------------------------------------------------ boot */
  var primaryWidget = null;

  // Open the search modal from an external control (e.g. the header search
  // icon). Bound once on the document in the capture phase so it runs before
  // the theme's own `.js-search-header` handler and can suppress the default
  // header search drawer.
  function bindExternalTriggers() {
    if (document.__idamTriggerBound) return;
    document.__idamTriggerBound = true;
    document.addEventListener('click', function (e) {
      var target = e.target;
      var trigger = target && target.closest
        ? target.closest('.js-search-header, [data-idam-search-trigger]')
        : null;
      if (!trigger || !primaryWidget) return; // not configured -> let theme handle it
      e.preventDefault();
      e.stopImmediatePropagation();
      primaryWidget.open();
    }, true);
  }

  function boot() {
    var roots = document.querySelectorAll('[data-idam-search]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__idamSearchBooted) return;
      root.__idamSearchBooted = true;
      var widget = new SearchWidget(root);
      widget.init();
      if (!primaryWidget && widget.baseUrl) primaryWidget = widget;
    });
    bindExternalTriggers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
